import { useEffect, useMemo, useState } from "react";
import { DatePicker, Drawer } from "antd";
import PropTypes from "prop-types";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { Button, Input, Select } from "../../../shared";
import styles from "./ReportFiltersDrawer.module.css";

const EMPTY_FILTERS = {};
const FILTER_LABELS = {
  transaction_type: AppStrings.TYPE,
  platform: AppStrings.PLATFORM,
  geographic_subdivision: AppStrings.STATE,
  payment_status: AppStrings.STATUS,
  money_type: AppStrings.MONEY_TYPE,
};
const NUMBER_PATTERN = /^\d+(?:\.\d+)?$/;

const normalizeOption = (option) =>
  typeof option === "string"
    ? { value: option, label: option }
    : {
        value: option?.value,
        label: option?.label ?? option?.value,
      };

const descriptorOptions = (filterMeta, key) => {
  const descriptor = filterMeta.find((filter) => filter?.key === key);
  return Array.isArray(descriptor?.options)
    ? descriptor.options.map(normalizeOption).filter((option) => option.value)
    : [];
};

function FilterField({ label, children }) {
  return (
    <label className={styles.field}>
      <span>{label}</span>
      {children}
    </label>
  );
}

export default function ReportFiltersDrawer({
  open,
  onClose,
  dates,
  appliedFilters = EMPTY_FILTERS,
  filterMeta = [],
  onApply,
}) {
  const [draftDates, setDraftDates] = useState(dates);
  const [draftFilters, setDraftFilters] = useState(appliedFilters);

  useEffect(() => {
    if (!open) return;
    setDraftDates(dates);
    setDraftFilters(appliedFilters);
  }, [appliedFilters, dates, open]);

  const selectFields = useMemo(
    () =>
      Object.keys(FILTER_LABELS).map((key) => ({
        key,
        label: FILTER_LABELS[key],
        options: descriptorOptions(filterMeta, key),
      })),
    [filterMeta],
  );

  const minimumText = draftFilters.amount_min;
  const maximumText = draftFilters.amount_max;
  const hasInvalidMinimum =
    minimumText !== undefined && !NUMBER_PATTERN.test(minimumText);
  const hasInvalidMaximum =
    maximumText !== undefined && !NUMBER_PATTERN.test(maximumText);
  const hasInvalidRange =
    !hasInvalidMinimum &&
    !hasInvalidMaximum &&
    minimumText !== undefined &&
    maximumText !== undefined &&
    Number(maximumText) < Number(minimumText);
  const hasValidDateRange = Boolean(
    draftDates?.[0] &&
      draftDates?.[1] &&
      !draftDates[0].isAfter(draftDates[1], "day"),
  );
  const canApply =
    hasValidDateRange &&
    !hasInvalidMinimum &&
    !hasInvalidMaximum &&
    !hasInvalidRange;

  const updateFilter = (key, value) => {
    setDraftFilters((current) => {
      const next = { ...current };
      if (value === undefined || value === null || value === "") {
        delete next[key];
      } else {
        next[key] = String(value);
      }
      return next;
    });
  };

  const handleClear = () => {
    setDraftDates(dates);
    setDraftFilters({});
    onApply({ dates, filters: {} });
    onClose();
  };

  const handleApply = () => {
    if (!canApply) return;
    onApply({ dates: draftDates, filters: draftFilters });
    onClose();
  };

  const footer = (
    <div className={styles.footer}>
      <Button
        intent="neutral"
        variant="outline"
        size="medium"
        onClick={handleClear}
      >
        {AppStrings.CLEAR}
      </Button>
      <Button
        intent="primary"
        variant="outline"
        size="medium"
        disabled={!canApply}
        onClick={handleApply}
      >
        {AppStrings.APPLY}
      </Button>
    </div>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={AppStrings.PAM_REPORT_FILTERS}
      placement="right"
      width={400}
      destroyOnHidden
      footer={footer}
      className={styles.drawer}
      classNames={{
        body: styles.body,
        footer: styles.drawerFooter,
      }}
      data-testid="report-filters-drawer"
    >
      <div className={styles.dateRow}>
        <FilterField label={AppStrings.FROM}>
          <DatePicker
            className={styles.datePicker}
            showNow={false}
            value={draftDates?.[0] ?? null}
            onChange={(date) =>
              setDraftDates([date, draftDates?.[1] ?? null])
            }
          />
        </FilterField>
        <FilterField label={AppStrings.TO}>
          <DatePicker
            className={styles.datePicker}
            showNow={false}
            value={draftDates?.[1] ?? null}
            onChange={(date) =>
              setDraftDates([draftDates?.[0] ?? null, date])
            }
          />
        </FilterField>
      </div>

      {selectFields.map(({ key, label, options }) => (
        <FilterField key={key} label={label}>
          <Select
            allowClear
            showSearch
            value={draftFilters[key]}
            options={options}
            placeholder={AppStrings.SELECT}
            aria-label={label}
            onChange={(value) => updateFilter(key, value)}
          />
        </FilterField>
      ))}

      <FilterField label={AppStrings.USER_ID}>
        <Input
          value={draftFilters.user_id ?? ""}
          placeholder={AppStrings.PAM_REPORT_SEARCH_PLACEHOLDER}
          aria-label={AppStrings.USER_ID}
          onChange={(event) => updateFilter("user_id", event.target.value)}
        />
      </FilterField>

      <div className={styles.amountRow}>
        <FilterField label={AppStrings.PAM_REPORT_MIN_PAYMENT_AMOUNT}>
          <Input
            inputMode="decimal"
            tone={hasInvalidMinimum ? "error" : "default"}
            value={minimumText ?? ""}
            placeholder={AppStrings.PAM_REPORT_AMOUNT_PLACEHOLDER}
            onChange={(event) =>
              updateFilter("amount_min", event.target.value)
            }
          />
        </FilterField>
        <FilterField label={AppStrings.PAM_REPORT_MAX_PAYMENT_AMOUNT}>
          <Input
            inputMode="decimal"
            tone={hasInvalidMaximum || hasInvalidRange ? "error" : "default"}
            value={maximumText ?? ""}
            placeholder={AppStrings.PAM_REPORT_AMOUNT_PLACEHOLDER}
            onChange={(event) =>
              updateFilter("amount_max", event.target.value)
            }
          />
        </FilterField>
      </div>

      {hasInvalidRange && (
        <span className={styles.error}>
          {AppStrings.PAYMENTS_LIMITS_MAX_MUST_BE_GTE_MIN}
        </span>
      )}
    </Drawer>
  );
}

FilterField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

ReportFiltersDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dates: PropTypes.arrayOf(PropTypes.object).isRequired,
  appliedFilters: PropTypes.object,
  filterMeta: PropTypes.arrayOf(PropTypes.object),
  onApply: PropTypes.func.isRequired,
};
